import { getCustomerSites, listCustomers } from "../../services/customer/customer.service";
import type { CustomerSiteSummary } from "../../services/customer/customer.types";
import type { CategoryAssociatedCustomer, CategorySummary } from "../../services/category/category.types";

export const getSiteRegistryId = (site: CustomerSiteSummary): number =>
    site.siteIdentifierRegistryId ?? site.siteId ?? site.id;

export const getCategoryErrorMessage = (error: unknown, fallback: string): string =>
    error instanceof Error && error.message ? error.message : fallback;

export function hasAssignedSites(category: CategorySummary): boolean {
    return (category.associatedSiteCount ?? 0) > 0 || !!category.associatedSites?.length;
}

export async function addAssociatedCustomers(categories: CategorySummary[]) {
    const errors: string[] = [];
    const customersBySite = new Map<number, CategoryAssociatedCustomer>();
    let complete = true;
    if (categories.some(category => category.associatedSites?.length)) {
        try {
            const response = await listCustomers();
            if (!response.success || !response.data) {
                throw new Error(response.error?.message || "Failed to load category customers");
            }
            await Promise.all(response.data.map(async customer => {
                try {
                    const sites = await getCustomerSites(customer.id);
                    if (!sites.success || !sites.data) {
                        throw new Error(sites.error?.message || `Failed to load sites for "${customer.customerName}"`);
                    }
                    sites.data.forEach(site => customersBySite.set(getSiteRegistryId(site), {
                        id: customer.id, name: customer.customerName,
                    }));
                } catch (error: unknown) {
                    complete = false;
                    errors.push(getCategoryErrorMessage(error, "Failed to load category customer sites"));
                }
            }));
        } catch (error: unknown) {
            complete = false;
            errors.push(getCategoryErrorMessage(error, "Failed to load category customers"));
        }
    }
    return {
        categories: categories.map(category => {
            if (!complete && category.associatedSites?.length) {
                return { ...category, associatedCustomers: undefined };
            }
            const customers = new Map<number, CategoryAssociatedCustomer>();
            category.associatedSites?.forEach(site => {
                const customer = customersBySite.get(site.siteIdentifierRegistryId);
                if (customer) customers.set(customer.id, customer);
            });
            return { ...category, associatedCustomers: [...customers.values()] };
        }),
        errors,
    };
}
